import { findReservoirByName, getReservoirById, listReservoirs, toTankenReservoirConfig } from './reservoirCatalog.js'

const HYDRO_API_URL = (process.env.VUE_APP_HYDRO_API_URL || '').replace(/\/$/, '')
const REQUIRE_API = process.env.VUE_APP_HYDRO_REQUIRE_API === 'true'

function wave(seed, index, amplitude) {
  return Math.sin((index + seed) / 24 * Math.PI * 2) * amplitude
}

function round(value, digits = 2) {
  const base = Math.pow(10, digits)
  return Math.round(Number(value) * base) / base
}

function defaultInflow(hours = 24, peak = 2380, base = 760) {
  const values = []
  const center = hours * 0.42
  const width = Math.max(hours * 0.16, 1)
  for (let i = 0; i < hours; i++) {
    const hydrograph = base + (peak - base) * Math.exp(-Math.pow(i - center, 2) / (2 * Math.pow(width, 2)))
    values.push(round(Math.max(0, hydrograph + wave(3, i, 90)), 1))
  }
  return values
}

function times(hours) {
  const now = new Date()
  now.setMinutes(0, 0, 0)
  return Array.from({ length: hours }, (_, i) => {
    const d = new Date(now.getTime() + i * 3600 * 1000)
    return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:00`
  })
}

async function postHydro(path, body, fallback) {
  if (HYDRO_API_URL !== 'local') {
    try {
      const response = await fetch(`${HYDRO_API_URL}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
      })
      if (response.ok) return response.json()
      if (REQUIRE_API) throw new Error(`${path} responded ${response.status}`)
    } catch (error) {
      if (REQUIRE_API) throw error
      console.warn('[hydroService] backend unavailable, using local demo result:', error)
    }
  } else if (REQUIRE_API) {
    throw new Error(`${path} requires API but VUE_APP_HYDRO_API_URL=local`)
  }
  return fallback(body || {})
}

async function requestHydro(path, options = {}) {
  if (HYDRO_API_URL === 'local') {
    throw new Error('Hydro API disabled by VUE_APP_HYDRO_API_URL=local')
  }
  const response = await fetch(`${HYDRO_API_URL}${path}`, {
    method: options.method || 'GET',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    body: options.body ? JSON.stringify(options.body) : undefined,
  })
  if (!response.ok) {
    let detail = ''
    let errorCode = ''
    try {
      const rawBody = await response.text()
      const payload = rawBody ? JSON.parse(rawBody) : {}
      detail = payload?.detail?.message || payload?.detail?.error_code || payload?.message || ''
      errorCode = payload?.detail?.error_code || payload?.error_code || ''
    } catch (_) {
      detail = `${path} responded ${response.status}`
    }
    const requestError = new Error(detail || `${path} responded ${response.status}`)
    requestError.code = errorCode || 'ANALYSIS_SERVICE_FAILED'
    requestError.status = response.status
    throw requestError
  }
  return response.json()
}

/**
 * 比赛动作使用的严格洪水分析请求。
 *
 * 路由必须显式配置到既有真实 FastAPI/业务接口；此函数只复用现有 HTTP
 * 请求逻辑，不允许 hydroService 的本地演示 fallback。
 */
export async function runFloodAnalysisStrict(payload = {}) {
  const path = (process.env.VUE_APP_FLOOD_ANALYSIS_PATH || '').trim()
  if (!path || path === 'local') {
    const error = new Error('VUE_APP_FLOOD_ANALYSIS_PATH is not configured')
    error.code = 'ANALYSIS_SERVICE_UNAVAILABLE'
    throw error
  }
  try {
    return await requestHydro(path, { method: 'POST', body: payload })
  } catch (error) {
    if (!error.code) error.code = 'ANALYSIS_SERVICE_UNAVAILABLE'
    throw error
  }
}

export function generateForecast(payload = {}) {
  return postHydro('/hydro/forecast/run', payload, localForecast)
}

export async function fetchForecastModels() {
  try {
    return await requestHydro('/hydro/forecast/models')
  } catch (error) {
    if (REQUIRE_API) throw error
    console.warn('[hydroService] forecast model registry unavailable, using local contract list:', error)
    return localForecastModels()
  }
}

export function compareDispatch(payload = {}) {
  return postHydro('/hydro/dispatch/compare', payload, localCompareDispatch)
}

export function runFloodScenario(payload = {}) {
  return postHydro('/hydro/flood/scenario', payload, localFloodScenario)
}

export function createDispatchScheme(payload = {}) {
  return requestHydro('/hydro/dispatch/schemes', { method: 'POST', body: payload })
}

export function submitDispatchScheme(schemeId, payload = {}) {
  return requestHydro(`/hydro/dispatch/schemes/${schemeId}/submit`, { method: 'POST', body: payload })
}

export function approveDispatchScheme(schemeId, payload = {}) {
  return requestHydro(`/hydro/dispatch/schemes/${schemeId}/approve`, { method: 'POST', body: payload })
}

export function cancelDispatchScheme(schemeId, payload = {}) {
  return requestHydro(`/hydro/dispatch/schemes/${schemeId}/cancel`, { method: 'POST', body: payload })
}

export function issueDispatchCommand(payload = {}) {
  return requestHydro('/hydro/dispatch/commands', { method: 'POST', body: payload })
}

export function recordDispatchReceipt(commandId, payload = {}) {
  return requestHydro(`/hydro/dispatch/commands/${commandId}/receipt`, { method: 'POST', body: payload })
}

export function retryDispatchCommand(commandId, payload = {}) {
  return requestHydro(`/hydro/dispatch/commands/${commandId}/retry`, { method: 'POST', body: payload })
}

export function fetchDispatchAudit() {
  return requestHydro('/hydro/dispatch/audit')
}

export { findReservoirByName, getReservoirById, listReservoirs, toTankenReservoirConfig }

export function getReservoirModelConfig(nameOrId = 'guxian') {
  const reservoir = getReservoirById(nameOrId)
  if (reservoir.id === nameOrId) return toTankenReservoirConfig(reservoir)
  return toTankenReservoirConfig(findReservoirByName(nameOrId))
}

export async function runHuadongForecast(payload = {}) {
  return postHuadong('/huadong/forecast', buildHuadongPathRequest(payload), () => localHuadongForecast(payload))
}

export async function runHuadongAnalysis(payload = {}) {
  return postHuadong('/huadong/analysis', buildHuadongPathRequest(payload, {
    options: { column: 'streamflow', ...(payload.options || {}) },
  }), () => localHuadongAnalysis(payload))
}

export async function runHuadongEnsemble(payload = {}) {
  return postHuadong('/huadong/ensemble', buildHuadongPathRequest(payload, {
    file_path: payload.file_path,
    options: {
      method: payload.method || 'bma',
      observation_dataset: payload.observation_dataset || defaultHuadongDatasetPath(),
      observation_column: payload.observation_column || 'streamflow',
      ...(payload.options || {}),
    },
  }), () => localHuadongEnsemble(payload))
}

export async function runHuadongCorrection(payload = {}) {
  return postHuadong('/huadong/correction', buildHuadongPathRequest(payload, {
    file_path: payload.file_path,
    options: {
      observation_dataset: payload.observation_dataset || defaultHuadongDatasetPath(),
      observation_column: payload.observation_column || 'streamflow',
      ...(payload.options || {}),
    },
  }), () => localHuadongCorrection(payload))
}

export async function runHuadongRisk(payload = {}) {
  return postHuadong('/huadong/risk', buildHuadongPathRequest(payload, {
    file_path: payload.file_path,
    options: {
      thresholds: payload.thresholds || { flood: 300, severe: 500 },
      model_columns: payload.model_columns || ['corrected_forecast'],
      ...(payload.options || {}),
    },
  }), () => localHuadongRisk(payload))
}

export async function runHuadongWarning(payload = {}) {
  return postHuadong('/huadong/warning', buildHuadongPathRequest(payload, {
    file_path: payload.file_path,
    options: {
      forecast_column: payload.forecast_column || 'corrected_forecast',
      warning_threshold: Number(payload.warning_threshold || 300),
      lead_time_hours: Number(payload.lead_time_hours || 24),
      ...(payload.options || {}),
    },
  }), () => localHuadongWarning(payload))
}

export async function runHuadongModelChain(payload = {}) {
  if (HYDRO_API_URL === 'local') {
    if (REQUIRE_API) throw new Error('Huadong model chain requires API but VUE_APP_HYDRO_API_URL=local')
    return localHuadongModelChain(payload)
  }
  try {
    const datasetPath = payload.dataset_path || defaultHuadongDatasetPath()
    const forecast = await runHuadongForecast({ ...payload, dataset_path: datasetPath })
    const forecastCsv = getArtifactPath(forecast, 'forecast')
    if (!forecastCsv) throw new Error('Huadong forecast did not return forecast artifact')

    const analysis = await runHuadongAnalysis({ ...payload, dataset_path: datasetPath })
    const ensemble = await runHuadongEnsemble({
      ...payload,
      file_path: forecastCsv,
      observation_dataset: datasetPath,
    })
    const ensembleCsv = getArtifactPath(ensemble, 'ensemble')
    if (!ensembleCsv) throw new Error('Huadong ensemble did not return ensemble artifact')

    const correction = await runHuadongCorrection({
      ...payload,
      file_path: ensembleCsv,
      observation_dataset: datasetPath,
    })
    const correctedCsv = getArtifactPath(correction, 'correction')
    if (!correctedCsv) throw new Error('Huadong correction did not return correction artifact')

    const risk = await runHuadongRisk({ ...payload, file_path: correctedCsv })
    const warning = await runHuadongWarning({ ...payload, file_path: correctedCsv })
    return normalizeHuadongChain({ forecast, analysis, ensemble, correction, risk, warning }, 'api')
  } catch (error) {
    if (REQUIRE_API) throw error
    console.warn('[hydroService] Huadong chain unavailable, using local demo result:', error)
    return localHuadongModelChain(payload)
  }
}

async function postHuadong(path, body, fallback) {
  if (HYDRO_API_URL !== 'local') {
    try {
      const response = await fetch(`${HYDRO_API_URL}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
      })
      if (response.ok) return response.json()
      throw new Error(`${path} responded ${response.status}`)
    } catch (error) {
      if (REQUIRE_API) throw error
      console.warn(`[hydroService] ${path} unavailable, using local demo result:`, error)
    }
  } else if (REQUIRE_API) {
    throw new Error(`${path} requires API but VUE_APP_HYDRO_API_URL=local`)
  }
  return fallback(body || {})
}

function defaultHuadongDatasetPath() {
  return process.env.VUE_APP_HUADONG_DATASET_PATH
    || 'D:/aaa_hydro/Multimodal-Smart-Collaborative-Interaction-System/guxian_vue/HydroModels/HuadongCode/data/basin_001_hourly.csv'
}

function defaultHuadongOutputRoot() {
  return process.env.VUE_APP_HUADONG_OUTPUT_ROOT || undefined
}

function buildHuadongPathRequest(payload = {}, overrides = {}) {
  const options = { ...(payload.options || {}), ...(overrides.options || {}) }
  return {
    dataset_path: overrides.dataset_path !== undefined ? overrides.dataset_path : (payload.dataset_path || defaultHuadongDatasetPath()),
    file_path: overrides.file_path !== undefined ? overrides.file_path : payload.file_path,
    output_root: overrides.output_root !== undefined ? overrides.output_root : (payload.output_root || defaultHuadongOutputRoot()),
    options,
  }
}

function getArtifactPath(result, key) {
  return result && result.artifact_paths && result.artifact_paths[key]
}

function normalizeHuadongChain(parts, source = 'api') {
  const warningText = parts.warning && (parts.warning.small_summary || parts.warning.summary_text || '')
  const riskText = parts.risk && (parts.risk.small_summary || parts.risk.summary_text || '')
  const forecastText = parts.forecast && (parts.forecast.small_summary || parts.forecast.summary_text || '')
  const analysisText = parts.analysis && (parts.analysis.small_summary || parts.analysis.summary_text || '')
  const ensembleText = parts.ensemble && (parts.ensemble.small_summary || parts.ensemble.summary_text || '')
  const correctionText = parts.correction && (parts.correction.small_summary || parts.correction.summary_text || '')
  const warningLevel = inferWarningLevel(warningText)
  return {
    source,
    status: 'completed',
    generated_at: new Date().toLocaleString('zh-CN'),
    forecast: parts.forecast,
    analysis: parts.analysis,
    ensemble: parts.ensemble,
    correction: parts.correction,
    risk: parts.risk,
    warning: parts.warning,
    metrics: {
      forecast_summary: forecastText,
      analysis_summary: analysisText,
      ensemble_summary: ensembleText,
      correction_summary: correctionText,
      risk_summary: riskText,
      warning_summary: warningText,
      warning_level: warningLevel,
      warning_level_cn: warningLevelCn(warningLevel),
    },
    expert_summary: buildHuadongExpertSummary({
      source,
      forecastText,
      analysisText,
      ensembleText,
      correctionText,
      riskText,
      warningText,
      warningLevel,
    }),
  }
}

function inferWarningLevel(text = '') {
  const lower = String(text).toLowerCase()
  if (String(text).includes('红色')) return 'red'
  if (String(text).includes('橙色')) return 'orange'
  if (String(text).includes('黄色')) return 'yellow'
  if (String(text).includes('蓝色')) return 'blue'
  if (lower.includes('red')) return 'red'
  if (lower.includes('orange')) return 'orange'
  if (lower.includes('yellow')) return 'yellow'
  if (lower.includes('blue')) return 'blue'
  return 'normal'
}

function warningLevelCn(level) {
  return ({ red: '红色预警', orange: '橙色预警', yellow: '黄色预警', blue: '蓝色预警', normal: '无明显预警' })[level] || '无明显预警'
}

function buildHuadongExpertSummary({ source, forecastText, analysisText, ensembleText, correctionText, riskText, warningText, warningLevel }) {
  const sourceLabel = source === 'api' ? '已调用华东水文模型真实接口' : '接口未启动，已切换本地演示模型'
  return `${sourceLabel}完成未来24小时水文研判。四模型预报结果：${forecastText || '新安江、GR4J、RF、LSTM 已完成并行预报'}。趋势诊断：${analysisText || '当前流量过程存在阶段性波动'}。集合预报：${ensembleText || '多模型结果已完成融合'}。误差订正：${correctionText || '已完成偏差订正'}。风险分析：${riskText || '已计算超阈概率和分位风险'}。预警结论：${warningText || warningLevelCn(warningLevel)}，综合预警等级为${warningLevelCn(warningLevel)}。`
}

function localHuadongForecast(payload = {}) {
  const forecast = localForecast({ ...payload, horizon_hours: payload.horizon_hours || 24, peak_flow: payload.peak_flow || 2460, base_flow: payload.base_flow || 720 })
  return {
    status: 'completed',
    operation: 'forecast',
    source: 'local',
    small_summary: `新安江、GR4J、随机森林和LSTM四模型已完成${forecast.horizon_hours}小时预报，综合RMSE为${forecast.metrics.rmse.toFixed(2)}，预报等级${forecast.metrics.grade}。`,
    artifact_paths: {},
    data: forecast,
  }
}

function localHuadongAnalysis(payload = {}) {
  return {
    status: 'completed',
    operation: 'analysis',
    source: 'local',
    small_summary: `${payload.station || '古贤-龙门区间'}流量序列完成趋势、周期和突变分析：24小时均值约1386.4 m³/s，先涨后落，主周期约1天，洪峰附近识别到突变点。`,
    artifact_paths: {},
  }
}

function localHuadongEnsemble() {
  return {
    status: 'completed',
    operation: 'ensemble',
    source: 'local',
    small_summary: '四模型已采用贝叶斯模型平均法完成集合预报，模型一致性系数0.82，结果可作为调度会商主参考。',
    artifact_paths: {},
  }
}

function localHuadongCorrection() {
  return {
    status: 'completed',
    operation: 'correction',
    source: 'local',
    small_summary: '误差订正已完成，NSE为0.88，偏差18.6 m³/s，RMSE为96.4；已修正系统偏差并将洪峰到达时间校正1小时。',
    artifact_paths: {},
  }
}

function localHuadongRisk(payload = {}) {
  const peak = Number(payload.peak_flow || 5200)
  const probability = peak >= 9800 ? 0.93 : peak >= 7400 ? 0.82 : peak >= 6200 ? 0.74 : 0.68
  const p50 = round(peak * 0.46, 0)
  const p90 = round(peak * 0.55, 0)
  return {
    status: 'completed',
    operation: 'risk',
    source: 'local',
    small_summary: `超阈概率分析显示洪水风险最高，概率约${probability}；P50洪峰约${p50} m³/s，P90洪峰约${p90} m³/s。`,
    artifact_paths: {},
  }
}

function localHuadongWarning(payload = {}) {
  const peak = Number(payload.peak_flow || 5200)
  const level = peak >= 9000 ? '红色' : peak >= 7000 ? '橙色' : peak >= 5600 ? '黄色' : '蓝色'
  return {
    status: 'completed',
    operation: 'warning',
    source: 'local',
    small_summary: `未来24小时洪水预警等级为${level}预警，未识别干旱风险。`,
    artifact_paths: {},
  }
}

function localHuadongModelChain(payload = {}) {
  return normalizeHuadongChain({
    forecast: localHuadongForecast(payload),
    analysis: localHuadongAnalysis(payload),
    ensemble: localHuadongEnsemble(payload),
    correction: localHuadongCorrection(payload),
    risk: localHuadongRisk(payload),
    warning: localHuadongWarning(payload),
  }, 'local')
}

function localForecast(payload) {
  const horizon = Number(payload.horizon_hours || 24)
  const p50 = defaultInflow(horizon, Number(payload.peak_flow || 2380), Number(payload.base_flow || 760))
  return {
    station: payload.station || '古贤水利枢纽',
    scenario: payload.scenario || '汛期来水',
    horizon_hours: horizon,
    times: times(horizon),
    observed: p50.slice(0, 8).map((v, i) => round(v * (0.96 + 0.04 * Math.sin(i)), 1)),
    forecast: {
      p10: p50.map(v => round(v * 0.84, 1)),
      p50,
      p90: p50.map(v => round(v * 1.18, 1)),
    },
    metrics: {
      qr: 0.91,
      dc: 0.86,
      nse: 0.88,
      mae: 76.4,
      rmse: 103.7,
      peak_flow: Math.max(...p50),
      peak_time: times(horizon)[p50.indexOf(Math.max(...p50))],
      grade: '甲级',
    },
  }
}

function localForecastModels() {
  const models = [
    { model_id: 'xaj', name: 'XAJ rainfall-runoff model', family: 'conceptual_hydrology', status: 'available', implementation: 'local_fallback_contract' },
    { model_id: 'gr4j', name: 'GR4J runoff model', family: 'conceptual_hydrology', status: 'available', implementation: 'local_fallback_contract' },
    { model_id: 'rf', name: 'Random Forest forecast model', family: 'machine_learning', status: 'available', implementation: 'local_fallback_contract' },
    { model_id: 'lstm', name: 'LSTM inflow forecast model', family: 'deep_learning', status: 'available', implementation: 'huadong_chain_or_local_fallback' },
    { model_id: 'transformer', name: 'Transformer sequence forecast contract', family: 'deep_learning', status: 'contract_stub', implementation: 'api_contract_pending_training_artifacts' },
    { model_id: 'dlinear', name: 'DLinear long-horizon forecast contract', family: 'deep_learning', status: 'contract_stub', implementation: 'api_contract_pending_training_artifacts' },
    { model_id: 'patchtst', name: 'PatchTST long-horizon forecast contract', family: 'deep_learning', status: 'contract_stub', implementation: 'api_contract_pending_training_artifacts' },
  ]
  return {
    count: models.length,
    available: models.filter(item => item.status === 'available').map(item => item.model_id),
    contract_stubs: models.filter(item => item.status === 'contract_stub').map(item => item.model_id),
    models,
    source: 'frontend_fallback_contract',
  }
}

function localCompareDispatch(payload) {
  const reservoir = payload.reservoir_id
    ? getReservoirById(payload.reservoir_id)
    : findReservoirByName(payload.reservoir_name || payload.station || '古贤')
  const inflow = payload.inflow || defaultInflow(Number(payload.horizon_hours || 24))
  const levels = reservoir.characteristic_levels
  const schemes = [
    runScheme({ ...payload, reservoir, inflow, mode: 'fixed', scheme_name: '方案A 固定下泄', fixed_release: 980 }),
    runScheme({ ...payload, reservoir, inflow, mode: 'pre_release', scheme_name: '方案B 防洪预泄', fixed_release: 1180 }),
    runScheme({ ...payload, reservoir, inflow, mode: 'compensation', scheme_name: '方案C 补偿调度', fixed_release: 860 }),
  ]
  const ranked = schemes.slice().sort((a, b) => a.metrics.risk_score - b.metrics.risk_score || b.metrics.peak_reduction_rate - a.metrics.peak_reduction_rate)
  return {
    reservoir: {
      id: reservoir.id,
      name: reservoir.name,
      levels,
      capacities: reservoir.capacities,
      sourceLevel: reservoir.sourceLevel,
    },
    inflow,
    schemes,
    recommended_scheme_id: ranked[0].scheme_id,
    summary: `推荐${ranked[0].scheme_name}，风险等级${ranked[0].metrics.risk_level}，削峰率${ranked[0].metrics.peak_reduction_rate}%。`,
  }
}

function runScheme(payload) {
  const reservoir = payload.reservoir || getReservoirById('guxian')
  const levels = reservoir.characteristic_levels
  const capacities = reservoir.capacities
  const inflow = payload.inflow || defaultInflow(24)
  const release = []
  const level = []
  let currentLevel = Number(payload.initial_level || reservoir.snapshot.level || levels.normal_level)
  const fixed = Number(payload.fixed_release || 980)
  const safe = Number(payload.safe_release || Math.min(reservoir.discharge_capacity_m3s || 1800, 1800))
  for (const qin of inflow) {
    let qout = fixed
    if (payload.mode === 'pre_release') qout = Math.max(fixed, qin * 0.72 + Math.max(currentLevel - 616.8, 0) * 190)
    if (payload.mode === 'compensation') qout = currentLevel > 617.5 ? Math.min(qin * 0.88 + 120, safe) : Math.min(fixed, qin * 0.55)
    qout = Math.max(180, Math.min(qout, safe * 1.18))
    currentLevel += (qin - qout) / 18000
    release.push(round(qout, 1))
    level.push(round(currentLevel, 2))
  }
  const peakInflow = Math.max(...inflow)
  const peakRelease = Math.max(...release)
  const maxLevel = Math.max(...level)
  const violations = []
  release.forEach((q, i) => { if (q > safe) violations.push({ time_index: i, type: 'safe_release', value: q, limit: safe }) })
  level.forEach((h, i) => { if (h > 618) violations.push({ time_index: i, type: 'flood_limit_level', value: h, limit: 618 }) })
  const peakReduction = peakInflow ? Math.max(0, (peakInflow - peakRelease) / peakInflow * 100) : 0
  const riskScore = Math.min(100, 18 + Math.max(0, maxLevel - levels.flood_limit_level) * 18 + violations.length * 4.5)
  const riskLevel = riskScore >= 75 ? '红色' : riskScore >= 50 ? '橙色' : riskScore >= 30 ? '黄色' : '蓝色'
  return {
    scheme_id: `${payload.mode}-${payload.scheme_name}`.replace(/[^\w]/g, '').slice(0, 28),
    scheme_name: payload.scheme_name,
    mode: payload.mode,
    times: times(inflow.length),
    curves: { inflow, release, level, storage: level.map(h => round(4200 + (h - 610) * 150, 2)) },
    reservoir: {
      id: reservoir.id,
      name: reservoir.name,
      sourceLevel: reservoir.sourceLevel,
    },
    constraints: {
      dead_level: levels.dead_level,
      normal_level: levels.normal_level,
      flood_limit_level: levels.flood_limit_level,
      design_level: levels.design_flood_level,
      check_flood_level: levels.check_flood_level,
      total_capacity: capacities.total_capacity,
      flood_capacity: capacities.flood_capacity,
      safe_release: safe,
      ecological_release: 180,
    },
    metrics: {
      max_level: round(maxLevel, 2),
      peak_inflow: round(peakInflow, 1),
      peak_release: round(peakRelease, 1),
      peak_reduction_rate: round(peakReduction, 1),
      flood_storage_used_mcm: round(Math.max(0, maxLevel - levels.flood_limit_level) * 150, 2),
      violation_count: violations.length,
      risk_score: round(riskScore, 1),
      risk_level: riskLevel,
    },
    violations,
    recommendation: violations.length
      ? `${payload.scheme_name}存在${violations.length}项约束风险，建议降低初始水位或提高预泄流量。`
      : `${payload.scheme_name}满足主要约束，最高水位低于汛限${round(618 - maxLevel, 2)}m，削峰率${round(peakReduction, 1)}%。`,
  }
}

function localFloodScenario(payload) {
  const reservoir = payload.reservoir_id
    ? getReservoirById(payload.reservoir_id)
    : findReservoirByName(payload.reservoir_name || payload.station || '古贤')
  const dispatch = payload.dispatch_result || runScheme(payload)
  const release = dispatch.curves.release
  const downstream = route(release)
  const frames = downstream.map((flow, index) => {
    const stage = round(456 + Math.max(flow - 700, 0) / 420, 2)
    const impacted = impactedObjects(stage)
    return {
      index,
      flow: round(flow, 1),
      stage,
      area_km2: round(Math.max(0.08, Math.pow(stage - 456 + 0.2, 1.45) * 0.86), 2),
      risk_level: floodRisk(stage, impacted),
      polygon: demoPolygon(stage),
    }
  })
  const maxStage = Math.max(...frames.map(f => f.stage))
  const impacted = impactedObjects(maxStage)
  return {
    scenario_id: payload.scenario_id || 'flood-demo',
    scenario_name: payload.scenario_name || '龙门断面洪水演进',
    reservoir: {
      id: reservoir.id,
      name: reservoir.name,
      levels: reservoir.characteristic_levels,
      capacities: reservoir.capacities,
      sourceLevel: reservoir.sourceLevel,
    },
    times: dispatch.times.slice(0, downstream.length),
    upstream_release: release,
    downstream_flow: downstream.map(v => round(v, 1)),
    frames,
    metrics: {
      peak_flow: round(Math.max(...downstream), 1),
      peak_time: dispatch.times[downstream.indexOf(Math.max(...downstream))],
      max_stage: round(maxStage, 2),
      max_inundation_area_km2: Math.max(...frames.map(f => f.area_km2)),
      impacted_count: impacted.length,
      evacuation_people: impacted.reduce((s, item) => s + item.people, 0),
      risk_level: floodRisk(maxStage, impacted),
    },
    impacted_objects: impacted,
    recommended_plan: {
      name: impacted.length ? '防汛应急联动预案' : '常规巡查预案',
      actions: impacted.length ? ['启动防汛会商', '通知下游责任人', '预置抢险物资'] : ['维持监测频率', '滚动复核来水预报'],
    },
  }
}

function route(inflow) {
  if (!inflow.length) return []
  const out = [inflow[0] * 0.92]
  for (let i = 1; i < inflow.length; i++) out.push(Math.max(0, inflow[i] * 0.28 + inflow[i - 1] * 0.34 + out[i - 1] * 0.38))
  return out
}

function impactedObjects(stage) {
  return [
    { name: '龙门居民区', type: '居民区', threshold: 460.0, people: 1260, asset: '村镇与道路' },
    { name: '下游农田', type: '农田', threshold: 458.6, people: 0, asset: '3.4 万亩农田' },
    { name: '输水管线交口', type: '管线', threshold: 459.4, people: 18, asset: '输水管线节点' },
    { name: '泵站前池', type: '泵站', threshold: 461.2, people: 42, asset: '泵站及配电房' },
  ].filter(item => stage >= item.threshold).map(item => ({ ...item, exceedance_m: round(stage - item.threshold, 2) }))
}

function floodRisk(stage, impacted) {
  if (stage >= 462 || impacted.some(item => item.type === '居民区')) return '红色'
  if (stage >= 460 || impacted.length) return '橙色'
  if (stage >= 458) return '黄色'
  return '蓝色'
}

function demoPolygon(stage) {
  const spread = Math.min(Math.max((stage - 456) * 0.0018, 0.001), 0.014)
  return [
    [110.3426 - spread, 35.4991 - spread],
    [110.3418 - spread, 35.5012 + spread],
    [110.3473 + spread, 35.5032 + spread],
    [110.3479 + spread, 35.5010 - spread],
  ]
}
